import { _, object, bind, unique, filter, pluck, isFunction, each, has, some, findIndex } from 'underscore';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  localize,
  FilterStorageType,
  FilterBarIngoreClickOutsideClassName,
  convertFilterEntityToExpressions,
} from '@bingads-webui/filter-commons';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { FilterBar } from '@bingads-webui/filter-bar-react';
import { PredicateListReadonly } from '@bingads-webui/predicate-list-readonly-react';
import { PredicateListUneditable } from '@bingads-webui/predicate-list-uneditable-react';

const getStyles = () => ({
  predicateList: {
    flex: '1 !important',
    minWidth: '398px',
  },
});

class FilterViewManagerUnthemed extends React.PureComponent {
  constructor(props) {
    super(props);
    this.filterService = props.filterService;
    this.entitySchema = localize(
      this.filterService.entitySchema,
      props.schemaI18n,
      props.legacyI18n
    );

    this.state = {
      showFilterBar: false,
      selectableFilter: null,
      editableFilter: null,
      openedPredicateIndex: null,
      savedFilterNames: null,
      recentFilters: [],
      key: 0,
      isFilterChanged: false,
    };

    this.refreshSavedFilters();
    if (this.filterService.isRecentFilterSupported) {
      this.refreshRecentFilters();
    }

    if (this.props.searchbox) {
      this.setupSearchboxFilterOnToolbar();
    }

    this.filterService.p$Initialized.then(() => {
      const quickFilter = this.filterService.getQuickFilter();
      const editableFilter = this.filterService.getEditableFilter();
      this.setState({
        selectableFilter: quickFilter
          ? this.filterService.createFilterEntity(quickFilter.value.query) : null,
        editableFilter: editableFilter
          ? this.filterService.createFilterEntity(editableFilter.query) : null,
        openedPredicateIndex: null,
      });
    });

    this.filterService.on('invalid-filter-property', ({
      originalFilter,
      validatedFilter,
    }) => {
      if (props.instrumentation && isFunction(props.instrumentation.writeRawLog)) {
        props.instrumentation.writeRawLog({
          logCategory: 'ApplicationErrorClientScript',
          api: '',
          message: `Invalid filter property. Original filter is ${JSON.stringify(originalFilter)}. Validated filter is ${JSON.stringify(validatedFilter)}`,
          entryType: 'Message',
        });
      }
    });

    this.filterService.on('filter-state-changed', bind((combinedFilter, subStatus) => {
      if (subStatus === 'quick-filter-applied') {
        const selectableFilter = this.filterService.getQuickFilter();
        this.setState({
          selectableFilter: selectableFilter
            ? this.filterService.createFilterEntity(selectableFilter.value.query) : null,
          openedPredicateIndex: null,
        });
      }
      if (subStatus === 'editable-filter-applied' || subStatus === 'editable-filter-removed') {
        this.setState({
          editableFilter: this.filterService.getEditableFilter(),
          openedPredicateIndex: null,
        });
      }
      if (subStatus === 'filter-reset') {
        this.setState({
          editableFilter: null,
          selectableFilter: null,
          openedPredicateIndex: null,
        });
      }
    }), this);

    this.filterService.on('remove-saved-filter-name', (filterNameToRemove) => {
      this.setState(state => ({
        savedFilterNames: filter(state.savedFilterNames, name => name !== filterNameToRemove),
      }));
    });

    this.filterService.on('have-invalid-or-removed-properties', ({
      deletedProperties,
      invalidProperties,
      showFilterBar,
    }) => {
      each(deletedProperties, (deletedColumn) => {
        const property = this.entitySchema.properties[deletedColumn];

        if (property) {
          property.isDeletedProperty = true;
        }
      });

      each(invalidProperties, (inapplicableColumn) => {
        const property = this.entitySchema.properties[inapplicableColumn];

        if (property) {
          property.isInvalidProperty = true;
        }
      });

      const { editableFilter } = this.state;
      const filerExpressions = convertFilterEntityToExpressions(editableFilter);
      const hasDeletedProperties = some(
        deletedProperties,
        deleted => findIndex(filerExpressions, expression => has(expression, deleted)) >= 0
      );
      const hasInvalidProperties = some(
        invalidProperties,
        invalid => findIndex(filerExpressions, expression => has(expression, invalid)) >= 0
      );

      if ((hasDeletedProperties || hasInvalidProperties) && showFilterBar) {
        const isFilterBarShown = this.state.showFilterBar;

        this.setState({
          openedPredicateIndex: null,
          showFilterBar: true,
        });
        this.props.resetToolbarVisibility({
          forceHideToolbar: true,
        });

        if (isFilterBarShown) {
          // If it's already shown, force update.
          this.setState((state) => {
            this.setState({ key: state.key + 1 });
          });
        }
      }
    });
  }

  onEditableFilterUpdate = (query) => {
    if (!this.state.isFilterChanged) {
      this.setState({
        isFilterChanged: true,
      });
    }
    this.filterService.setEditableFilter(query);
  };

  onSelectableFilterUpdate = (query) => {
    if (!this.state.isFilterChanged) {
      this.setState({
        isFilterChanged: true,
      });
    }
    this.filterService.setQuickFilter('', {
      query,
    });
  };

  onSaveFilter = (savedFilterName) => {
    const promises = [];
    if (this.state.selectableFilter) {
      promises.push(this.filterService.addUpdateSavedFilter(
        this.state.selectableFilter.query,
        savedFilterName,
        FilterStorageType.SelectableFilter,
        false
      ));
    }
    if (this.state.editableFilter) {
      promises.push(this.filterService.addUpdateSavedFilter(
        this.state.editableFilter.query,
        savedFilterName,
        FilterStorageType.EditableFilter,
        false
      ));
    }
    Promise.all(promises).then(() => {
      this.refreshSavedFilters();
    });
  };

  onDeleteFilters = (savedFilterName) => {
    this.setState(state => ({
      savedFilterNames: filter(state.savedFilterNames, name => name !== savedFilterName),
    }));
    this.filterService.deleteSavedFilter({
      name: savedFilterName,
    }, FilterStorageType.SelectableFilter);
    this.filterService.deleteSavedFilter({
      name: savedFilterName,
    }, FilterStorageType.EditableFilter);
  };

  onReset = () => {
    this.filterService.removeAppliedFilter();
    this.setState({
      editableFilter: null,
      selectableFilter: null,
      openedPredicateIndex: null,
      isFilterChanged: false,
    });
    this.hideFilterBar(false);
    if (isFunction(this.props.updateWaterfallFilter)) {
      this.props.updateWaterfallFilter(null);
    }
  };

  onApplyRecentFilter = (index) => {
    const filters = this.filterService.getRecentFiltersByIndex(index);
    this.applySavedFilter(filters);
  };

  onApplySavedFilter = (savedFilterName) => {
    Promise.all([
      this.filterService.getFilterByName(savedFilterName, FilterStorageType.SelectableFilter),
      this.filterService.getFilterByName(savedFilterName, FilterStorageType.EditableFilter),
    ]).then((filters) => {
      if (this.props.beforeApplySavedFilter) {
        this.props.beforeApplySavedFilter(filters).then(() => {
          this.applySavedFilter(filters);
        });
      } else {
        this.applySavedFilter(filters);
      }
    });
  };

  setupPredicateListOnToolbar = () => {
    const viewStateFilterPillsEnabled = _.result(this.props.permissions.dynamic, 'ToolbarViewStateFilterPills', false);
    const ViewStatePredicateList = viewStateFilterPillsEnabled
      ? PredicateListUneditable : PredicateListReadonly;

    const predicateListView = (
      <ViewStatePredicateList
        selector={this.props.selector}
        entitySchema={this.entitySchema}
        i18n={this.props.i18n}
        selectableFilter={this.state.selectableFilter}
        editableFilter={this.state.editableFilter}
        onClickPredicate={(openedPredicateIndex) => {
          this.setState({
            openedPredicateIndex,
            showFilterBar: true,
          });
          this.props.resetToolbarVisibility({
            forceHideToolbar: true,
          });
        }}
        onSelectableFilterUpdate={this.onSelectableFilterUpdate}
        onEditableFilterUpdate={viewStateFilterPillsEnabled && this.onEditableFilterUpdate}
        hideAddFilterButton={this.props.hideAddFilterButton}
        getPredicateIconClassName={this.props.getPredicateIconClassName}
        predicateValueNameGetter={this.props.predicateValueNameGetter}
      />);
    const newItem = {
      id: 'predicate-list',
      type: 'custom',
      content: predicateListView,
      classes: this.props.classes.predicateList,
    };
    this.props.updateToolbar(newItem);
  };

  setupSearchboxFilterOnToolbar = () => {
    const newItem = {
      id: 'search-box-filter',
      type: 'search-box',
      placeholder: this.props.searchbox.placeholder,
      handleChange: (value) => {
        if (value) {
          const query = object(
            [this.props.searchbox.searchProperty],
            [object([this.props.searchbox.searchOperator || '$contains'], [value])]
          );
          this.onEditableFilterUpdate(query);
        }
      },
    };
    this.props.updateToolbar(newItem);
  };

  applySavedFilter = (filters) => {
    if (!this.state.isFilterChanged) {
      this.setState({
        isFilterChanged: true,
      });
    }
    this.filterService.setQuickFilter('', {
      query: filters[0] && filters[0].query ? filters[0].query : null,
    });
    this.filterService.setEditableFilter(filters[1]);
  };

  refreshSavedFilters() {
    this.filterService.getSavedFilters().then((savedFilters) => {
      this.setState({
        savedFilterNames: unique(pluck(savedFilters, 'name')),
      });
    });
  }

  refreshRecentFilters() {
    this.filterService.getRecentFilters().then((recentFilters) => {
      this.setState({
        recentFilters,
      });
    });
  }

  hideFilterBar = (updateRecentFilter = true) => {
    if (this.state.isFilterChanged && updateRecentFilter
      && this.filterService.isRecentFilterSupported) {
      this.setState({
        isFilterChanged: false,
      });
      this.filterService.addUpdateRecentFilter(
        this.state.selectableFilter && this.state.selectableFilter.query,
        FilterStorageType.SelectableFilter
      ).then(() => {
        this.filterService.addUpdateRecentFilter(this.state.editableFilter &&
          this.state.editableFilter.query, FilterStorageType.EditableFilter);
      });
    }
    this.setState({
      showFilterBar: false,
    });
    this.props.resetToolbarVisibility({
      forceHideToolbar: false,
    });
  };

  render() {
    if (this.state.showFilterBar) {
      return (
        <Fragment>
          <FilterBar
            key={this.state.key}
            entitySchema={this.entitySchema}
            i18n={this.props.i18n}
            legacyI18n={this.props.legacyI18n}
            selectableFilter={this.state.selectableFilter}
            editableFilter={this.state.editableFilter}
            openedPredicateIndex={this.state.openedPredicateIndex}
            readOnlyProperties={this.props.readOnlyProperties}
            disallowDuplicateFilters={this.props.disallowDuplicateFilters}
            onEditableFilterUpdate={this.onEditableFilterUpdate}
            onSelectableFilterUpdate={this.onSelectableFilterUpdate}
            selector={this.props.selector}
            hideFilterBar={this.hideFilterBar}
            onSaveFilter={this.onSaveFilter}
            savedFilters={this.state.savedFilterNames}
            recentFilters={this.state.recentFilters}
            onReset={this.onReset}
            onDeleteFilters={this.onDeleteFilters}
            outsideClickIgnoreClass={FilterBarIngoreClickOutsideClassName}
            updateWaterfallFilter={this.props.updateWaterfallFilter}
            onApplySavedFilter={this.onApplySavedFilter}
            onApplyRecentFilter={this.onApplyRecentFilter}
            isPoPEnabled={this.props.isPoPEnabled}
            hideSaveFilterButton={this.props.hideSaveFilterButton}
            disabledSaveFilterButtonWithReason={this.props.disabledSaveFilterButtonWithReason}
            hideAddFilterButton={this.props.hideAddFilterButton}
            getPredicateIconClassName={this.props.getPredicateIconClassName}
            predicateValueNameGetter={this.props.predicateValueNameGetter}
            permissions={this.props.permissions}
          />
        </Fragment>
      );
    }

    // Wait until we fetch quick and editable filter from storage
    // before set up predicate list.
    this.filterService.p$Initialized.then(() => {
      this.setupPredicateListOnToolbar();
    });

    return null;
  }
}

FilterViewManagerUnthemed.propTypes = {
  schemaI18n: PropTypes.shape({
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }),
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  updateToolbar: PropTypes.func.isRequired,
  filterService: PropTypes.shape({
    entitySchema: PropTypes.shape({
      name: PropTypes.string,
    }),
    getEditableFilter: PropTypes.func,
  }).isRequired,
  searchbox: PropTypes.shape({
    searchProperty: PropTypes.string,
    searchOperator: PropTypes.string,
    placeholder: PropTypes.string,
  }).isRequired,
  forcedFilter: PropTypes.arrayOf(PropTypes.object),// eslint-disable-line
  resetToolbarVisibility: PropTypes.func.isRequired,
  selector: PropTypes.arrayOf(PropTypes.shape({
    property: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
    })),
    removable: PropTypes.bool,
    defaultValue: PropTypes.arrayOf(PropTypes.string),
  })),
  updateWaterfallFilter: PropTypes.func,
  classes: PropTypes.shape({
    predicateList: PropTypes.string.isRequired,
  }).isRequired,
  isPoPEnabled: PropTypes.bool,
  readOnlyProperties: PropTypes.arrayOf(PropTypes.string),
  disallowDuplicateFilters: PropTypes.bool,
  hideSaveFilterButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  disabledSaveFilterButtonWithReason: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  hideAddFilterButton: PropTypes.bool,
  instrumentation: PropTypes.shape({
    writeRawLog: PropTypes.func,
  }),
  getPredicateIconClassName: PropTypes.func,
  beforeApplySavedFilter: PropTypes.func,
  predicateValueNameGetter: PropTypes.func,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
};

FilterViewManagerUnthemed.defaultProps = {
  selector: [],
  updateWaterfallFilter: null,
  isPoPEnabled: false,
  readOnlyProperties: [],
  hideSaveFilterButton: false,
  disabledSaveFilterButtonWithReason: '',
  hideAddFilterButton: false,
  instrumentation: null,
  disallowDuplicateFilters: false,
  getPredicateIconClassName: undefined,
  beforeApplySavedFilter: undefined,
  predicateValueNameGetter: undefined,
  permissions: { dynamic: {} },
};

export const FilterViewManager =
  withInstrumentation(withDefaultStyles(FilterViewManagerUnthemed, getStyles));
